import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useCompanySettings } from '@/contexts/CompanySettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import GlobalSearchDialog from '@/components/search/GlobalSearchDialog';
import NotificationBell from '@/components/common/NotificationBell';
import UserBadge from '@/components/common/UserBadge';
import {
  LayoutDashboard,
  Users,
  UserCog,
  Package,
  FileText,
  DollarSign,
  Building2,
  Settings,
  Lock,
  Search,
  LogOut,
  Menu,
  X,
  CalendarClock,
  Receipt,
  FileBarChart,
  MessageCircle,
  ScrollText,
  Layers,
  Coins,
  ClipboardList,
  KeyRound,
  IdCard as IdCardIcon,
  Building,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardLayout() {
  const { user, logout, refreshUser } = useAuth();
  const { can } = usePermissions();
  const { settings } = useCompanySettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwBusy, setPwBusy] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Fetch full user (with branch_name) once on mount if not already present
    if (user && !user.branch_name && user.branch_id) {
      axios.get(`${API_URL}/api/auth/me`, { withCredentials: true })
        .then(({ data }) => { if (refreshUser) refreshUser(data); })
        .catch(() => { /* ignore */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitChangePassword = async () => {
    if (!pw.current_password) return toast.error('Current password is required');
    if (!pw.new_password || pw.new_password.length < 8) return toast.error('New password must be at least 8 characters');
    if (pw.new_password !== pw.confirm_password) return toast.error('New passwords do not match');
    setPwBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPwOpen(false);
      setPw({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwBusy(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', check: () => true },
    { icon: Users, label: 'Customers', path: '/customers', check: () => can('customers', 'view') },
    { icon: ClipboardList, label: 'Approvals', path: '/customer-approvals', check: () => ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role) },
    { icon: Package, label: 'Inventory', path: '/inventory', check: () => can('inventory', 'view') },
    { icon: CalendarClock, label: 'Installments', path: '/installments', check: () => can('agreements', 'view') || can('recovery', 'view') },
    { icon: Layers, label: 'Plan Master', path: '/installment-plans', check: () => user?.role === 'Super Admin' || user?.role === 'Admin' },
    { icon: Receipt, label: 'Finance', path: '/finance', check: () => can('finance', 'view') },
    { icon: Coins, label: 'Investors', path: '/investors', check: () => ['Super Admin', 'Admin', 'Accountant'].includes(user?.role) },
    { icon: Coins, label: 'Commissions', path: '/commissions', check: () => (
      ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role)
      || (user?.role === 'Sales Executive' && user?.show_commission)
    ) },
    { icon: ClipboardList, label: 'Payroll', path: '/payroll', check: () => ['Super Admin', 'Admin', 'Accountant'].includes(user?.role) },
    { icon: ClipboardList, label: 'HR & Payroll', path: '/hr-payroll', check: () => ['Super Admin', 'Admin', 'Accountant'].includes(user?.role) },
    { icon: FileBarChart, label: 'Reports', path: '/reports', check: () => can('reports', 'view') },
    { icon: FileBarChart, label: 'Performance', path: '/performance', check: () => ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role) },
    { icon: MessageCircle, label: 'WhatsApp', path: '/whatsapp', check: () => can('recovery', 'view') || can('customers', 'view') },
    { icon: MessageCircle, label: 'WhatsApp Web', path: '/whatsapp-integration', check: () => ['Super Admin', 'Admin'].includes(user?.role) },
    { icon: Lock, label: 'Lock/Unlock', path: '/lock-unlock', check: () => can('lock_unlock', 'view') },
    { icon: UserCog, label: 'Users', path: '/users', check: () => can('users', 'view') },
    { icon: Building2, label: 'Branches', path: '/branches', check: () => can('branches', 'view') },
    { icon: ScrollText, label: 'Audit Log', path: '/audit-logs', check: () => user?.role === 'Super Admin' || user?.role === 'Admin' },
    { icon: Settings, label: 'Settings', path: '/settings', check: () => can('settings', 'view') },
  ];

  const filteredMenu = menuItems.filter((item) => item.check());

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-950 dark">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-slate-900 border-b border-slate-800 z-50">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="toggle-sidebar-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-white hover:bg-slate-800"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="flex items-center space-x-3">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 bg-amber-500 rounded-sm"></div>
              )}
              <div>
                <h1 className="text-lg font-bold text-white" data-testid="dashboard-company-name">
                  {settings.company_name || 'MANHA DIGITAL'}
                </h1>
                <p className="text-xs text-amber-500">{settings.subtitle || 'Your Dreams, Our Deals'}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="global-search-button"
              className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-sm text-slate-400 hover:text-white transition-colors text-sm min-w-[240px]"
            >
              <Search className="h-4 w-4" />
              <span>Search anything...</span>
              <kbd className="ml-auto text-xs bg-slate-900 border border-slate-700 px-1.5 py-0.5 rounded-sm">⌘K</kbd>
            </button>
            <Button
              variant="ghost" size="icon"
              onClick={() => setSearchOpen(true)}
              data-testid="mobile-search-button"
              className="md:hidden text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <Search className="h-5 w-5" />
            </Button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-3 text-sm text-slate-200 hover:bg-slate-800/60 rounded-sm pl-1 pr-2 py-1 transition-colors"
                >
                  {user?.photo_url ? (
                    <img src={user.photo_url} alt={user?.name} data-testid="header-user-photo"
                      className="h-9 w-9 md:h-11 md:w-11 rounded-full object-cover border-2 border-amber-500/70 shadow-md" />
                  ) : (
                    <div data-testid="header-user-photo"
                      className="h-9 w-9 md:h-11 md:w-11 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center text-slate-300 text-sm font-semibold">
                      {(user?.name || 'U').split(' ').map((x) => x[0]).slice(0, 2).join('')}
                    </div>
                  )}
                  <div className="hidden md:flex flex-col leading-tight text-left">
                    <span data-testid="user-name" className="font-semibold text-white text-sm">{user?.name}</span>
                    <span data-testid="user-role" className="text-xs text-amber-500">{user?.role}</span>
                  </div>
                  <ChevronDown className="hidden md:block h-4 w-4 text-slate-400 ml-1" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="w-72 bg-slate-900 border-slate-800 text-white"
                data-testid="user-menu-dropdown"
              >
                {/* Profile summary */}
                <div className="flex items-center gap-3 p-3 border-b border-slate-800">
                  {user?.photo_url ? (
                    <img src={user.photo_url} alt={user?.name} data-testid="menu-user-photo"
                      className="h-14 w-14 rounded-full object-cover border-2 border-amber-500/70" />
                  ) : (
                    <div data-testid="menu-user-photo"
                      className="h-14 w-14 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center text-slate-300 text-base font-semibold">
                      {(user?.name || 'U').split(' ').map((x) => x[0]).slice(0, 2).join('')}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div data-testid="menu-user-name" className="font-semibold text-white truncate">{user?.name}</div>
                    <div data-testid="menu-user-role" className="text-xs text-amber-500">{user?.role}</div>
                    {user?.designation && (
                      <div className="text-xs text-slate-400 truncate">{user.designation}</div>
                    )}
                    {user?.badge && (
                      <div className="mt-1">
                        <UserBadge badge={user.badge} size="sm" testId="menu-user-badge" />
                      </div>
                    )}
                  </div>
                </div>
                {/* Meta rows */}
                <div className="px-3 py-2 space-y-1.5 text-xs">
                  <div className="flex items-center gap-2 text-slate-400">
                    <IdCardIcon className="h-3.5 w-3.5" />
                    <span>Employee Code:</span>
                    <span data-testid="menu-user-employee-id" className="text-white font-medium ml-auto">
                      {user?.employee_id || '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Building className="h-3.5 w-3.5" />
                    <span>Branch:</span>
                    <span data-testid="menu-user-branch" className="text-white font-medium ml-auto truncate">
                      {user?.branch_name || (user?.branch_id ? 'Assigned' : 'Head Office')}
                    </span>
                  </div>
                </div>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem
                  data-testid="menu-change-password"
                  onSelect={(e) => { e.preventDefault(); setPwOpen(true); }}
                  className="cursor-pointer focus:bg-slate-800 focus:text-white"
                >
                  <KeyRound className="h-4 w-4 mr-2 text-amber-500" /> Change Password
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onSelect={handleLogout}
                  className="cursor-pointer focus:bg-slate-800 focus:text-red-300 text-red-400"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-button"
              onClick={handleLogout}
              className="md:hidden text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Change Password dialog */}
      <Dialog open={pwOpen} onOpenChange={(o) => { setPwOpen(o); if (!o) setPw({ current_password: '', new_password: '', confirm_password: '' }); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-amber-500" /> Change Password
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Enter your current password and pick a new one (minimum 8 characters).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs">Current Password *</Label>
              <Input type="password" value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                autoComplete="current-password"
                className="bg-slate-800 border-slate-700 text-white" data-testid="pw-current" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">New Password *</Label>
              <Input type="password" value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                autoComplete="new-password"
                className="bg-slate-800 border-slate-700 text-white" data-testid="pw-new" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Confirm New Password *</Label>
              <Input type="password" value={pw.confirm_password}
                onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })}
                autoComplete="new-password"
                className="bg-slate-800 border-slate-700 text-white" data-testid="pw-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950"
              disabled={pwBusy}
              onClick={submitChangePassword} data-testid="pw-submit">
              {pwBusy ? 'Updating…' : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar */}
      <div
        className={`fixed top-16 left-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 transform transition-transform duration-200 z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="dashboard-sidebar"
      >
        <ScrollArea className="h-full">
          <div className="p-4 space-y-1">
            {filteredMenu.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-3 py-3 rounded-sm transition-all ${
                  isActive(item.path)
                    ? 'bg-amber-500 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="pt-16 lg:pl-64">
        <div className="p-6">
          <Outlet />
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
}